import { useNavigate, useSearchParams } from "@solidjs/router";
import { createResource, createSignal, Match, Show, Switch } from "solid-js";
import { CalendarIcon } from "@/shared/components/icons";
import { toast } from "@/shared/components/Toast";

/** Kept in step with the server, which refuses anything shorter. */
const MIN_LENGTH = 12;

type LinkOwner = { email: string; firstName: string };

/**
 * The page behind a one-time link. There is no self-service reset: an
 * administrator issues the link and passes it on, and this is where it is
 * spent. Like the login page it talks to /auth directly, since a caller here
 * has no token yet.
 */
export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = createSignal("");
  const [confirmation, setConfirmation] = createSignal("");
  const [error, setError] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  const linkToken = () => String(params.token ?? "");

  const [owner] = createResource(linkToken, async (token): Promise<LinkOwner | null> => {
    if (!token) return null;
    const response = await fetch(`/auth/reset/${encodeURIComponent(token)}`);
    if (!response.ok) return null;
    return (await response.json()) as LinkOwner;
  });

  const onSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    setError("");

    if (password() !== confirmation()) {
      setError("Those passwords do not match");
      return;
    }
    if (password().length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters`);
      return;
    }

    setBusy(true);
    try {
      const response = await fetch("/auth/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: linkToken(), password: password() }),
      });
      const body = (await response.json()) as { error?: string };
      if (!response.ok) {
        setError(body.error ?? "Could not set your password");
        return;
      }
      toast("Password set, you can sign in now");
      navigate("/login", { replace: true });
    } catch {
      setError("Connection error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="login-wrapper">
      <div class="login-card">
        <div class="logo" style={{ "justify-content": "center", "margin-bottom": "24px" }}>
          <CalendarIcon size={28} />
          <span>Business Manager</span>
        </div>

        <Switch>
          <Match when={owner.loading}>
            <p style={{ "text-align": "center", color: "var(--text-muted)" }}>Checking link…</p>
          </Match>

          <Match when={!owner()}>
            <p style={{ "text-align": "center", "margin-bottom": "16px" }}>
              This link is no longer valid. Ask an administrator to send a new one.
            </p>
            <button
              type="button"
              class="btn btn-ghost"
              style={{ width: "100%", "justify-content": "center" }}
              onClick={() => navigate("/login", { replace: true })}
            >
              Back to sign in
            </button>
          </Match>

          <Match when={owner()}>
            {(person) => (
              <form onSubmit={onSubmit}>
                <p
                  style={{
                    "text-align": "center",
                    color: "var(--text-muted)",
                    "font-size": "13px",
                    "margin-bottom": "20px",
                  }}
                >
                  Set a password for {person().email}
                </p>
                <div class="form-group">
                  <label for="new-password">New password</label>
                  <input
                    id="new-password"
                    type="password"
                    required
                    autofocus
                    autocomplete="new-password"
                    minLength={MIN_LENGTH}
                    value={password()}
                    onInput={(event) => setPassword(event.currentTarget.value)}
                  />
                </div>
                <div class="form-group">
                  <label for="confirm-password">Confirm password</label>
                  <input
                    id="confirm-password"
                    type="password"
                    required
                    autocomplete="new-password"
                    minLength={MIN_LENGTH}
                    value={confirmation()}
                    onInput={(event) => setConfirmation(event.currentTarget.value)}
                  />
                </div>
                <Show when={error()}>
                  <div
                    style={{
                      color: "var(--accent-red)",
                      "font-size": "13px",
                      "text-align": "center",
                      "margin-bottom": "12px",
                    }}
                  >
                    {error()}
                  </div>
                </Show>
                <button
                  type="submit"
                  class="btn btn-primary"
                  disabled={busy()}
                  style={{ width: "100%", "justify-content": "center", padding: "10px" }}
                >
                  {busy() ? "Saving…" : "Set password"}
                </button>
              </form>
            )}
          </Match>
        </Switch>
      </div>
    </div>
  );
}
